import {
  type AdminOrderDestination,
  formatSizeBreakdown,
  type OrderDetail,
  SHIPPING_REGION_LABELS,
} from '@repo/api-contracts';
import { type ReactElement, useId, useState } from 'react';
import { canChangeDestinationAddress, OrderFlagBadge } from '@/entities/order';
import { OrderAddressDialog } from '@/features/order/shipping';
import { formatCount, formatDateTime, type OrderActions } from '@/shared/lib';
import { Button, detailListStyles as styles } from '@/shared/ui';
import { OrderShipmentRow } from './order-shipment-row';

interface OrderDestinationBlockProps {
  readonly order: OrderDetail;
  readonly destination: AdminOrderDestination;
  /** 배송지 순서(0부터) — 화면 번호는 index + 1. */
  readonly index: number;
  /** 주문 전체 송장 수 — 주문의 마지막 송장은 지울 수 없다(송장 행의 삭제 버튼 기준). */
  readonly shipmentCount: number;
  readonly now: string;
  readonly actions: OrderActions;
  readonly onReload: () => void;
  readonly pending?: boolean;
}

/**
 * 배송지 한 곳(SUPER-86 §6-2) — 번호·지역·수량, 받는 분·주소·마지막 변경, [배송지 변경](그 배송지에 송장이 없고 배송 완료 전),
 * 그 배송지의 송장 목록. 출고 뒤 송장이 없는 배송지는 '송장 없는 배송지' 배지. 지역을 모르는 옛 배송지는 지역 자리에 '-'.
 */
export const OrderDestinationBlock = ({
  order,
  destination,
  index,
  shipmentCount,
  now,
  actions,
  onReload,
  pending,
}: OrderDestinationBlockProps): ReactElement => {
  const [editing, setEditing] = useState(false);
  const titleId = useId();
  const number = index + 1;
  const address = destination.shippingAddress;
  // 주문한 고객 본인이 바꿨으면 '고객', 운영자면 서버가 푼 표시 이름.
  const updatedBy =
    destination.addressUpdatedBy === order.userId
      ? '고객'
      : (destination.addressUpdatedByName ?? '—');
  const shipped = order.status === 'PAID' && order.fulfillmentStatus === 'SHIPPED';
  const notShipped = shipped && destination.shipments.length === 0;
  const regionLabel = destination.region ? SHIPPING_REGION_LABELS[destination.region] : '-';
  const sizeBreakdown = formatSizeBreakdown(destination.sizes);

  return (
    <li aria-labelledby={titleId} className="rounded-xl border border-foreground/[0.08] p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id={titleId} className={styles.subheading}>
          배송지 {number}
        </h3>
        <span className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
          {`${regionLabel} · ${formatCount(destination.quantity)}개`}
          {sizeBreakdown ? ` · ${sizeBreakdown}` : ''}
          {notShipped ? <OrderFlagBadge badge="DESTINATION_NOT_SHIPPED" /> : null}
        </span>
      </div>
      <dl className={`mt-3 ${styles.list}`}>
        <dt className={styles.term}>수령인</dt>
        <dd>
          {address.recipientName} · {address.recipientPhone}
        </dd>
        <dt className={styles.term}>주소</dt>
        <dd>
          ({address.postalCode}) {address.address1} {address.address2}
        </dd>
        <dt className={styles.term}>마지막 변경</dt>
        <dd>
          {destination.addressUpdatedAt ? (
            `${formatDateTime(destination.addressUpdatedAt)} · 변경한 사람 ${updatedBy}`
          ) : (
            <span className={styles.empty}>없음</span>
          )}
        </dd>
      </dl>
      {canChangeDestinationAddress(order, destination) ? (
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          aria-label={`배송지 ${number} 변경`}
          disabled={pending}
          onClick={() => setEditing(true)}
        >
          배송지 변경
        </Button>
      ) : null}
      <h4 className="mt-4 text-sm font-medium">송장</h4>
      {destination.shipments.length === 0 ? (
        <p className="mt-1 text-sm text-muted-foreground">송장 없음</p>
      ) : (
        <ul aria-label={`배송지 ${number} 송장`} className="mt-2 flex flex-col gap-3">
          {destination.shipments.map((shipment) => (
            <OrderShipmentRow
              key={shipment.id}
              shipment={shipment}
              count={shipmentCount}
              active={shipped}
              now={now}
              actions={actions}
              onReload={onReload}
              pending={pending}
            />
          ))}
        </ul>
      )}
      {editing ? (
        <OrderAddressDialog
          orderId={order.id}
          destination={destination}
          onSubmit={actions.updateShippingAddress}
          onReload={onReload}
          onClose={() => setEditing(false)}
          pending={pending}
        />
      ) : null}
    </li>
  );
};
